import {useState, useEffect, useMemo, useCallback, useRef} from 'react'
import {useCustomerStore} from '../../store/hooks/useStore'
import {DateUtils} from '../../utils/date-utils'
import {isServer} from '../../utils/utils'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'

type VoucherBannerPromotionResponse = {
  id: string
  title?: string
  description?: string
  calloutMsg?: string
  voucherCode?: string | undefined
  startDate?: string
  endDate?: string
  minSpend?: number
  maximumPerOrder?: number
  c_voucherEnableCTA?: boolean
}

export type BannerDisplayContent = {
  text: {
    title: string
    description: string
  }
  dates?: {
    startDate: string | undefined
    endDate: string | undefined
  }
  criteria?: {
    minSpend?: number
    maximumPerOrder?: string | number | undefined
  }
  discountCode: string | undefined
  enableCTA?: boolean
  additionalData?: {
    promotion_id: string
  }
}

export const useBannerHelper = () => {
  const [showVoucherBanner, setShowVoucherBanner] = useState<boolean>(false)
  const [bannerContent, setBannerContent] = useState<VoucherBannerPromotionResponse | null>(null)
  const {currentCustomerGroups} = useCustomerStore()
  const lastFetchedCustomerGroups = useRef<string>('')

  const fetchVoucherBannerPromotion = useCallback(async (customerGroups: Array<string>) => {
    try {
      const res: Response = await fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Promotions-GetPromotionForBanner`,
        {
          method: 'POST',
          body: JSON.stringify({customerGroups}),
          headers: {
            'Content-Type': 'application/json'
          }
        }
      )
      const response = await res.json()
      if (response !== undefined && response.id && response.c_rulesetJSON) {
        const ruleset = JSON.parse(response.c_rulesetJSON)
        if (ruleset.maxApp) {
          response.maximumPerOrder = ruleset.maxApp
        }
        return response
      }
      return undefined
    } catch (error) {
      console.error(`Failed to fetch voucher banner promotion`, error)
      return undefined
    }
  }, [])

  const constructedBannerContent: BannerDisplayContent = useMemo(() => {
    return {
      text: {
        title: bannerContent?.title || '',
        description: bannerContent?.description || bannerContent?.calloutMsg || ''
      },
      dates: {
        startDate: bannerContent?.startDate
          ? DateUtils.format(bannerContent.startDate, 'dd-MM-yyyy')
          : undefined,
        endDate: bannerContent?.endDate
          ? DateUtils.format(bannerContent.endDate, 'dd-MM-yyyy')
          : undefined
      },
      criteria: {
        minSpend: bannerContent?.minSpend,
        maximumPerOrder: bannerContent?.maximumPerOrder || undefined
      },
      discountCode: bannerContent?.voucherCode || undefined,
      enableCTA: bannerContent?.c_voucherEnableCTA || false,
      additionalData: {
        promotion_id: bannerContent?.id || ''
      }
    }
  }, [bannerContent])

  useEffect(() => {
    const customerGroupsKey = JSON.stringify(currentCustomerGroups || [])
    if (lastFetchedCustomerGroups.current === customerGroupsKey) {
      return
    }

    lastFetchedCustomerGroups.current = customerGroupsKey
    let fetchComplete = true

    async function getBannerContent() {
      const availableBanner = await fetchVoucherBannerPromotion(currentCustomerGroups)
      if (fetchComplete && availableBanner !== undefined) {
        setBannerContent(availableBanner)
      }
    }

    getBannerContent()

    return () => {
      fetchComplete = false
    }
  }, [fetchVoucherBannerPromotion, currentCustomerGroups])

  const dismissedStorage = !isServer
    ? sessionStorage.getItem('userHasDismissedVoucherBanner')
    : false

  const hideVoucherBanner = () => {
    setShowVoucherBanner(false)
    if (!isServer) {
      if (dismissedStorage) {
        sessionStorage.setItem(
          `userHasDismissedVoucherBanner`,
          `${dismissedStorage},${bannerContent?.id}`
        )
      } else {
        sessionStorage.setItem(`userHasDismissedVoucherBanner`, `${bannerContent?.id}`)
      }
    }
  }

  useEffect(() => {
    if (isServer) return

    const hasDismissed =
      (dismissedStorage ? dismissedStorage.split(',') : []).includes(bannerContent?.id || '') ||
      false
    if (!hasDismissed && bannerContent) {
      setShowVoucherBanner(true)
    }
  }, [bannerContent])

  return {
    showVoucherBanner,
    hideVoucherBanner,
    formattedBannerContent: constructedBannerContent
  }
}

// Voucher Banner should only be displayed on the following pages: Home, Basket, Product, Search, and Category pages.

export const shouldDisplayVoucherBanner = (pathname: string, children?: React.ReactNode) => {
  // We can check the following pages based on if they begin with the pathnames below

  const validVoucherSpecificPages = ['/basket', '/p/', '/search'].some((path) =>
    pathname.startsWith(path)
  )

  // To determine if the current page is a category page, we need to check if any of the children have a categoryId prop.
  // If so, we consider it a category page.

  const childArray = Array.isArray(children) ? children : children ? [children] : []
  const isCategoryPage = childArray.find((child: React.ReactElement) => child?.props?.children?.props?.categoryId) !== undefined

  // Finally we check /, as this is the homepage.

  return pathname === '/' || validVoucherSpecificPages || isCategoryPage
}
